import { create } from "zustand";
import { persist } from "zustand/middleware";

export type PreviewRole = "admin" | "medecin" | "global";

export interface PreviewMedecin {
  id: string;
  nom: string | null;
  prenom: string | null;
  email: string;
  /** Identifiant de spécialité (ex. rachis), aligné sur User.specialite côté API */
  specialite?: string | null;
  /**
   * Rôle réel du compte prévisualisé (`User.role`), tel que le renvoie `/user/medecins`.
   *
   * Ce n'est PAS `previewRole` : celui-ci reste « medecin » pour dire « l'œil imite une personne ».
   * Celui-là dit de quelle personne il s'agit.
   */
  role?: string | null;
  /**
   * Le compte prévisualisé est-il un relecteur de MAGE-X (médecin de l'organisation éditeur, ex-
   * fondateur) ? Il lit alors toute sa spécialité, un médecin ses seuls dossiers. Le backend fait la
   * même distinction (`owner_reviewer`) ; le front s'en sert pour ne pas promettre les dossiers d'un
   * praticien quand il en affiche une spécialité entière. Absent d'un état persisté plus ancien :
   * retomber sur la formulation « médecin » est le défaut sûr.
   */
  reviewer?: boolean | null;
}

interface PreviewViewState {
  /** Role utilisé pour la prévisualisation UI (sidebar, redirections). null = utiliser le rôle réel. */
  previewRole: PreviewRole | null;
  /** Quand previewRole === "medecin", id du médecin "simulé" (pour affichage). */
  previewMedecin: PreviewMedecin | null;
  /**
   * Spécialité que le lecteur a CHOISIE dans la barre latérale (« admin — rachis »,
   * « admin — pré-natal »...). Envoyée au backend via X-View-As-Specialite pour scoper les
   * données, et rien d'autre : le rôle admin est conservé. Simuler un rôle est le rôle de
   * `previewRole` (menu œil).
   *
   * Uniquement un choix : la spécialité d'un médecin prévisualisé n'atterrit pas ici, elle vit sur
   * `previewMedecin.specialite`. Les mélanger faisait fuiter une spécialité héritée dans un
   * périmètre choisi — sortir de « Vue médecin » laissait l'admin scopé sur la spécialité de ce
   * médecin — et faisait servir au tableau de bord toute la spécialité sous une bannière qui
   * promettait les dossiers d'un seul praticien. `utils/use-effective-specialite` répond à
   * « quelle spécialité ce lecteur lit-il », les deux cas compris.
   */
  previewSpecialite: string | null;
}

interface PreviewViewActions {
  setPreview: (
    payload:
      | {
          role: PreviewRole;
          medecin?: PreviewMedecin | null;
          specialite?: string | null;
        }
      | null,
  ) => void;
  /**
   * Choisir un périmètre de spécialité, sans toucher au rôle prévisualisé.
   *
   * Le sélecteur passait par `setPreview({ role: "admin", ... })`, ce qui convenait tant que seul
   * un admin y avait accès. Un fondateur (PT-74) s'y serait déclaré admin : tout le front lit
   * `previewRole ?? userRole`, il aurait donc affiché l'interface admin, que l'API — qui n'honore
   * `X-View-As-Role` que pour un admin — aurait refusée en 403. Choisir une spécialité répond à
   * « quel périmètre », jamais à « quel rôle ».
   */
  setSpecialite: (specialite: string | null) => void;
}

const initialState: PreviewViewState = {
  previewRole: null,
  previewMedecin: null,
  previewSpecialite: null,
};

export const usePreviewView = create<PreviewViewState & PreviewViewActions>()(
  persist(
    (set) => ({
      ...initialState,
      setPreview: (payload) =>
        set(
          payload === null
            ? initialState
            : {
                previewRole: payload.role,
                previewMedecin:
                  payload.role === "medecin" ? (payload.medecin ?? null) : null,
                // Ce que le lecteur a nommé, pour tous les rôles. Rien n'est déduit ici : la
                // spécialité d'un médecin prévisualisé se lit sur `previewMedecin`.
                previewSpecialite: payload.specialite ?? null,
              },
        ),
      setSpecialite: (specialite) => set({ previewSpecialite: specialite }),
    }),
    // v3: le sélecteur de spécialité ne rétrograde plus le rôle en « fondateur ». Un état v2
    // persisté porterait ce rôle et continuerait de masquer les outils admin ; la montée de
    // version le remet à zéro, ce qui est le défaut sûr (aucune prévisualisation).
    // v4: `previewSpecialite` ne porte plus qu'un choix. Un état v3 persisté pendant une « Vue
    // médecin » y porte une spécialité héritée, que v4 relirait comme choisie.
    // v5: les vues « fondateur » et « viewer » n'existent plus (PT-119). Un état v4 persisté sous
    // l'une d'elles enverrait un rôle que plus rien ne connaît ; la montée le remet à zéro.
    { name: "mage-x-preview-view", version: 5 },
  ),
);
